import {config} from "@/config/config.ts";
import {ReactNode, useEffect, useRef} from "react";

/**
 * Shared AdSense wrapper that triggers the ad push safely.
 *
 * The important job here is to NOT push while the <ins> is hidden or zero-width. Several ad slots
 * live in containers that are `display:none` until a wide breakpoint (e.g. the 1850px gutter rails),
 * or that haven't been laid out yet on first paint. Calling `adsbygoogle.push({})` against such an
 * element throws `TagError: No slot size for availableWidth=0` AND permanently marks the slot as
 * processed — so it can never fill afterwards, even once the container becomes visible. That single
 * bug silently kills the rails for essentially everyone below 1850px.
 *
 * So: wait until the unit has real rendered width before pushing, and if it starts hidden, watch it
 * with a ResizeObserver and push the moment it gains width (e.g. the viewport is widened past the
 * rail breakpoint). Skipped for subscribers (`user-subscribed` on <html>) and ad opt-outs.
 *
 * Wrap your `<ins class="adsbygoogle">` element with this component.
 */
export default function AdSensePush({children}: { children?: ReactNode }) {
    const wrapperRef = useRef<HTMLSpanElement>(null);
    const pushedRef = useRef(false);

    useEffect(() => {
        if (!config.adsenseEnabled) {
            return;
        }

        // Ensure the AdSense library is loaded before pushing. On public pages (landing / tool /
        // marketing routes) the app-shell loader in AppThemed never runs, so without this the
        // <ins> units queue a push into a plain array that nothing processes and never fill.
        // Idempotent with AppThemed via the shared script id, and skipped for subscribers
        // (AppThemed tags <html> with `user-subscribed`) and ad opt-outs.
        if (typeof document !== 'undefined'
            && window.location.hash !== '#no-tracking'
            && !(typeof localStorage !== 'undefined' && localStorage.disable_ads)
            && !document.documentElement.classList.contains('user-subscribed')) {
            const scriptId = 'adsbygoogle-' + config.adsenseClient;
            if (!document.getElementById(scriptId)) {
                const script = document.createElement('script');
                script.id = scriptId;
                script.async = true;
                script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.adsenseClient}`;
                script.crossOrigin = 'anonymous';
                document.head.appendChild(script);
            }
        }

        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }

        let observer: ResizeObserver | undefined;

        const attemptPush = () => {
            if (pushedRef.current) {
                return;
            }
            const ins = wrapper.querySelector<HTMLElement>('ins.adsbygoogle');
            if (!ins) {
                return;
            }
            // Hidden or not-yet-laid-out: offsetWidth is 0 (also covers display:none ancestors,
            // and getClientRects() is empty when nothing is rendered). Don't push yet — the
            // ResizeObserver below will call back once the unit actually has width.
            if (ins.offsetWidth < 1 || ins.getClientRects().length === 0) {
                return;
            }
            try {
                (window['adsbygoogle'] = window['adsbygoogle'] || []).push({});
            } catch (e) {
                console.error('AdSense push error:', e);
            }
            // Mark done regardless: a successful push fills the slot; a genuine error shouldn't be
            // retried in a loop. availableWidth=0 is already excluded by the width guard above.
            pushedRef.current = true;
            observer?.disconnect();

            // No-fill cleanup: when Google declines to serve (no iframe ~12s after the push —
            // covers both data-ad-status="unfilled" and the silent null-status case), collapse the
            // unit's container so readers aren't left staring at an "Advertisement" label over an
            // empty gap. The container is the nearest wrapper that also holds the label.
            setTimeout(() => {
                if (!ins.isConnected || ins.querySelector('iframe')) return;
                const container = ins.closest<HTMLElement>('aside, div');
                (container || ins).style.display = 'none';
            }, 12_000);
        };

        // First attempt after a tick so layout has settled. If the unit is still hidden/zero-width,
        // observe it and push as soon as it gains width.
        const timer = setTimeout(() => {
            attemptPush();
            if (!pushedRef.current && typeof ResizeObserver !== 'undefined') {
                const ins = wrapper.querySelector<HTMLElement>('ins.adsbygoogle');
                if (ins) {
                    observer = new ResizeObserver(() => attemptPush());
                    observer.observe(ins);
                }
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            observer?.disconnect();
        };
    }, []);

    if (!config.adsenseEnabled) {
        return null;
    }

    // display:contents => the wrapper generates no box, so the <ins>'s own sizing/positioning
    // (e.g. the fixed 160px rails) is completely unaffected.
    return <span ref={wrapperRef} style={{display: 'contents'}}>{children}</span>;
}
